/**
 * Strict DAG-CBOR decode (F6; float64 F7). Accepts exactly what @beacon/crypto's
 * dagCborEncode produces: definite lengths, no tags, string keys, canonical
 * float64 (0xfb) only (the encoder emits only CborFloat — cbor.ts
 * "determinism by construction"), 53-bit-safe ints. Canonical
 * ordering/minimality is enforced by re-encoding the decoded value and
 * requiring byte equality — the encoder stays the single source of truth for
 * "canonical" (ADR-12: one value, one byte string).
 */
import { dagCborEncode, CborFloat, type CborValue } from "@beacon/crypto";

export function dagCborDecode(bytes: Uint8Array): CborValue {
  const [v, end] = item(bytes, 0);
  if (end !== bytes.length) throw new Error("dag-cbor: trailing bytes");
  let re: Uint8Array;
  try {
    re = dagCborEncode(v);
  } catch (e) {
    throw new Error("dag-cbor: non-canonical encoding (re-encode rejected)", { cause: e });
  }
  if (re.length !== bytes.length || !re.every((b, i) => b === bytes[i])) {
    throw new Error("dag-cbor: non-canonical encoding");
  }
  return v;
}

const utf8Strict = new TextDecoder("utf-8", { fatal: true });

function item(b: Uint8Array, i: number): [CborValue, number] {
  if (i >= b.length) throw new Error("dag-cbor: truncated");
  const major = b[i] >> 5;
  const info = b[i] & 31;
  switch (major) {
    case 0: { const [n, j] = arg(b, i); return [num(n), j]; }
    case 1: { const [n, j] = arg(b, i); return [-1 - num(n), j]; }
    case 2: { const [n, j] = arg(b, i); const len = num(n); return [slice(b, j, len), j + len]; }
    case 3: { const [n, j] = arg(b, i); const len = num(n); return [utf8Strict.decode(slice(b, j, len)), j + len]; }
    case 4: {
      const [n, jj] = arg(b, i);
      const out: CborValue[] = [];
      let j = jj;
      for (let k = 0; k < num(n); k++) { const [v, nj] = item(b, j); out.push(v); j = nj; }
      return [out, j];
    }
    case 5: {
      const [n, jj] = arg(b, i);
      const out: Record<string, CborValue> = {};
      let j = jj;
      for (let k = 0; k < num(n); k++) {
        const [key, kj] = item(b, j);
        if (typeof key !== "string") throw new Error("dag-cbor: non-string map key");
        // `out[key] = v` on a plain object would hit the Object.prototype setter
        // for "__proto__" (prototype pollution), not store an own property.
        if (key === "__proto__") throw new Error("dag-cbor: forbidden map key");
        if (Object.prototype.hasOwnProperty.call(out, key)) throw new Error("dag-cbor: duplicate map key");
        const [v, vj] = item(b, kj);
        out[key] = v; j = vj;
      }
      return [out, j];
    }
    case 6: throw new Error("dag-cbor: tags not allowed");
    case 7:
      if (info === 20) return [false, i + 1];
      if (info === 21) return [true, i + 1];
      if (info === 22) return [null, i + 1];
      if (info === 27) {
        if (i + 9 > b.length) throw new Error("dag-cbor: truncated float64");
        const n = new DataView(b.buffer, b.byteOffset + i + 1, 8).getFloat64(0, false);
        if (!Number.isFinite(n)) throw new Error("dag-cbor: non-finite float forbidden");
        // -0.0 bit pattern is non-canonical (encoder normalizes to +0.0); the
        // re-encode guard also catches it, but reject explicitly for a clear error.
        if (Object.is(n, -0)) throw new Error("dag-cbor: non-canonical -0.0");
        return [new CborFloat(n), i + 9];
      }
      // float16 (25) / float32 (26) are non-canonical; other simples rejected.
      throw new Error(`dag-cbor: unsupported simple/float (info ${info})`);
    default: throw new Error("dag-cbor: unreachable");
  }
}

/** Read the additional-info argument as a BigInt; reject indefinite (31). */
function arg(b: Uint8Array, i: number): [bigint, number] {
  const info = b[i] & 31;
  if (info < 24) return [BigInt(info), i + 1];
  const len = info === 24 ? 1 : info === 25 ? 2 : info === 26 ? 4 : info === 27 ? 8 : -1;
  if (len < 0) throw new Error("dag-cbor: indefinite length not allowed");
  if (i + 1 + len > b.length) throw new Error("dag-cbor: truncated argument");
  let v = 0n;
  for (let k = 0; k < len; k++) v = (v << 8n) | BigInt(b[i + 1 + k]);
  return [v, i + 1 + len];
}

function num(n: bigint): number {
  if (n > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("dag-cbor: integer exceeds 2^53-1");
  return Number(n);
}

function slice(b: Uint8Array, start: number, len: number): Uint8Array {
  if (start + len > b.length) throw new Error("dag-cbor: truncated payload");
  return b.slice(start, start + len);
}
